<template>
	<div id="checkout-page" data-aos="fade">
		<!-- Confirmación -->
		<template v-if="submitted">
			<section class="success-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-md-8 col-lg-6 mx-auto text-center">
							<div class="success-icon mx-auto mb-4">
								<i class="fa-regular fa-check"></i>
							</div>
							<h2 class="success-title mb-2">¡Suscripción activada!</h2>
							<p class="success-code mb-4">Número de confirmación: UN00012983</p>
							<p class="success-welcome mb-2">
								Bienvenido al <strong>{{ plan.label }}</strong>.
							</p>
							<p class="success-lead mb-4">
								Recibirás un correo de confirmación con los detalles de tu plan en los próximos minutos.
							</p>
							<b-button to="/mi-cuenta" variant="dark" size="lg" class="checkout-cta checkout-cta--navy">
								Ir a mi cuenta
							</b-button>
						</div>
					</div>
				</div>
			</section>
		</template>

		<template v-else>
			<!-- Hero -->
			<section class="top-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-lg-8 mx-auto text-center">
							<p class="hero-eyebrow" data-aos="fade-up">Checkout</p>
							<h1 class="hero-title" data-aos="fade-up" data-aos-delay="50">
								Registro de <span class="text-accent">usuario</span>
							</h1>
						</div>
					</div>
				</div>
			</section>

			<section class="content-section">
				<div class="container">
					<!-- Stepper -->
					<div class="checkout-stepper d-flex align-items-center justify-content-center mb-5" data-aos="fade-up">
						<div class="stepper-item d-flex align-items-center">
							<div class="stepper-circle" :class="{ active: step >= 1, done: step > 1 }">
								<i v-if="step > 1" class="fa-regular fa-check"></i>
								<i v-else class="fa-regular fa-user"></i>
							</div>
							<span class="stepper-label" :class="{ active: step >= 1 }">Tu información</span>
						</div>

						<div class="stepper-line" :class="{ active: step >= 2 }"></div>

						<div class="stepper-item d-flex align-items-center">
							<div class="stepper-circle" :class="{ active: step >= 2 }">
								<i class="fa-regular fa-credit-card"></i>
							</div>
							<span class="stepper-label" :class="{ active: step >= 2 }">Pago</span>
						</div>
					</div>

					<div class="row align-items-start">
						<!-- Formulario -->
						<div class="col-lg-8 mb-4 mb-lg-0">
							<!-- Paso 1: Información -->
							<div v-if="step === 1" class="form-card" data-aos="fade-up">
								<h2 class="form-title mb-4">Tu información</h2>

								<b-form @submit.prevent="handleInfoSubmit">
									<div class="row">
										<div class="col-md-6">
											<b-form-group label="Nombre" label-for="nombre">
												<b-form-input
													id="nombre"
													v-model="info.nombre"
													placeholder="Juan"
													required
												></b-form-input>
											</b-form-group>
										</div>
										<div class="col-md-6">
											<b-form-group label="Apellido" label-for="apellido">
												<b-form-input
													id="apellido"
													v-model="info.apellido"
													placeholder="García"
													required
												></b-form-input>
											</b-form-group>
										</div>
									</div>

									<b-form-group label="Correo electrónico" label-for="email">
										<b-form-input
											id="email"
											v-model="info.email"
											type="email"
											placeholder="juan@empresa.com"
											required
										></b-form-input>
									</b-form-group>

									<b-form-group label="Teléfono" label-for="telefono">
										<b-form-input
											id="telefono"
											v-model="info.telefono"
											type="tel"
											placeholder="+52 55 0000 0000"
											required
										></b-form-input>
									</b-form-group>

									<b-form-group label-for="empresa">
										<template v-slot:label>
											Empresa <span class="text-muted font-weight-normal">(opcional)</span>
										</template>
										<b-form-input
											id="empresa"
											v-model="info.empresa"
											placeholder="Mi Empresa S.A. de C.V."
										></b-form-input>
									</b-form-group>

									<b-button type="submit" variant="dark" size="lg" block class="checkout-cta checkout-cta--navy mt-2">
										Continuar al pago
										<i class="fa-regular fa-chevron-right ml-2"></i>
									</b-button>
								</b-form>
							</div>

							<!-- Paso 2: Pago -->
							<div v-else class="form-card" data-aos="fade-up">
								<h2 class="form-title mb-2">Pago seguro</h2>
								<p class="secure-note mb-3">
									<i class="fa-regular fa-lock mr-2"></i>
									Tu información está cifrada y protegida
								</p>

								<div class="payment-brands mb-4">
									<i class="fa-brands fa-cc-amex" title="American Express"></i>
									<i class="fa-brands fa-cc-mastercard" title="Mastercard"></i>
									<i class="fa-brands fa-cc-visa" title="Visa"></i>
								</div>

								<b-form @submit.prevent="handlePagoSubmit">
									<b-form-group label="Nombre en la tarjeta" label-for="titular">
										<b-form-input
											id="titular"
											v-model="pago.titular"
											placeholder="Juan García"
											required
										></b-form-input>
									</b-form-group>

									<b-form-group label="Número de tarjeta" label-for="numero">
										<b-form-input
											id="numero"
											:value="pago.numero"
											placeholder="0000 0000 0000 0000"
											maxlength="19"
											required
											@input="formatCardNumber"
										></b-form-input>
									</b-form-group>

									<div class="row">
										<div class="col-6">
											<b-form-group label="Fecha de expiración" label-for="expiracion">
												<b-form-input
													id="expiracion"
													:value="pago.expiracion"
													placeholder="MM/AA"
													maxlength="5"
													required
													@input="formatExpiration"
												></b-form-input>
											</b-form-group>
										</div>
										<div class="col-6">
											<b-form-group label="CVV" label-for="cvv">
												<b-form-input
													id="cvv"
													:value="pago.cvv"
													placeholder="123"
													maxlength="4"
													required
													@input="formatCvv"
												></b-form-input>
											</b-form-group>
										</div>
									</div>

									<div class="d-flex payment-actions">
										<b-button
											type="button"
											variant="outline-secondary"
											size="lg"
											class="payment-back"
											@click="goBack"
										>
											Atrás
										</b-button>
										<b-button
											type="submit"
											variant="dark"
											size="lg"
											class="checkout-cta checkout-cta--navy flex-grow-1 ml-3"
										>
											<i class="fa-regular fa-lock mr-2"></i>
											Pagar {{ plan.precio }}
										</b-button>
									</div>
								</b-form>
							</div>
						</div>

						<!-- Resumen del plan -->
						<div class="col-lg-4">
							<div class="plan-summary" data-aos="fade-left">
								<p class="summary-eyebrow mb-1">Tu plan</p>
								<h3 class="summary-title mb-1">{{ plan.label }}</h3>
								<div class="summary-price mb-4">
									<span class="summary-amount">{{ plan.precio }}</span>
									<span class="summary-period">{{ plan.periodo }}</span>
								</div>

								<div class="summary-breakdown mb-4">
									<div class="d-flex justify-content-between summary-row mb-2">
										<span class="summary-muted">Subtotal</span>
										<span>{{ plan.precio }}</span>
									</div>
									<div class="d-flex justify-content-between summary-row">
										<span class="summary-muted">IVA (16%)</span>
										<span class="summary-iva">Calculado al pagar</span>
									</div>
								</div>

								<div class="summary-secure d-flex align-items-center mb-3">
									<i class="fa-regular fa-lock mr-2 flex-shrink-0"></i>
									<span>Pago 100% seguro y encriptado</span>
								</div>

								<div class="text-center">
									<router-link to="/precios" class="summary-change">
										Cambiar de plan
									</router-link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
const PLAN_LABELS = {
	mensual: { label: 'Plan Mensual', precio: '$299', periodo: '/mes' },
	semestral: { label: 'Plan Semestral', precio: '$1,499', periodo: '/6 meses' },
	anual: { label: 'Plan Anual', precio: '$2,399', periodo: '/año' },
};

export default {
	data() {
		return {
			step: 1,
			submitted: false,
			info: {
				nombre: '',
				apellido: '',
				email: '',
				telefono: '',
				empresa: '',
			},
			pago: {
				titular: '',
				numero: '',
				expiracion: '',
				cvv: '',
			},
		};
	},

	computed: {
		plan() {
			const planKey = (this.$route.query.plan || 'mensual').toString();
			return PLAN_LABELS[planKey] || PLAN_LABELS.mensual;
		},
	},

	methods: {
		handleInfoSubmit() {
			this.step = 2;
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},

		handlePagoSubmit() {
			this.submitted = true;
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},

		goBack() {
			this.step = 1;
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},

		formatCardNumber(value) {
			const digits = String(value || '').replace(/\D/g, '').slice(0, 16);
			this.pago.numero = digits.replace(/(.{4})/g, '$1 ').trim();
		},

		formatExpiration(value) {
			const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
			this.pago.expiracion = digits.replace(/^(\d{2})(\d)/, '$1/$2');
		},

		formatCvv(value) {
			this.pago.cvv = String(value || '').replace(/\D/g, '').slice(0, 4);
		},
	},
};
</script>
